<template>
  <div class="player-setup">
    <div class="content">
      <h1 class="title">Spelers</h1>
      <div class="table-wrap">
        <div class="felt">
          <span
            v-for="s in suits"
            :key="s.id"
            class="felt-suit"
            :class="{ red: s.red }"
          >{{ s.symbol }}</span>
        </div>
        <label
          v-for="(_, index) in players"
          :key="index"
          :for="'player-' + index"
          class="seat"
          :class="seatPositions[index]"
        >
          <span class="player-label">Speler {{ index + 1 }}</span>
          <input
            :id="'player-' + index"
            v-model="players[index]"
            type="text"
            placeholder="Naam invoeren"
            autocomplete="off"
            required
          />
        </label>
      </div>
      <button class="random-btn-ghost" aria-hidden="true" tabindex="-1">⇄ Willekeurig</button>
    </div>
    <AppFooter
      :can-continue="allFilled"
      :show-back="true"
      @continue="submit"
      @back="setView('start')"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useGameStore } from '../../stores/game'
import { SUITS } from '../../models/suits'
import AppFooter from '../shared/AppFooter.vue'

export default {
  name: 'PlayerSetup',
  components: { AppFooter },
  data() {
    return {
      players: ['', '', '', ''],
      seatPositions: ['top', 'right', 'bottom', 'left'],
      suits: SUITS
    }
  },
  created() {
    if (this.playerNames.length === 4) {
      this.players = [...this.playerNames]
    }
  },
  computed: {
    ...mapState(useGameStore, ['playerNames']),
    allFilled() {
      return this.players.every(p => p.trim() !== '')
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    ...mapActions(useGameStore, ['confirmPlayerNames', 'setView']),
    submit() {
      this.confirmPlayerNames(this.players)
    },
    handleKeydown(e) {
      if (e.key === 'Enter' && this.allFilled) this.submit()
      if (e.key === 'Escape') this.setView('start')
    }
  }
}
</script>

<style scoped>
.player-setup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  padding: 1.25rem;
  gap: 1.5rem;
}

.title {
  font-size: 1.75rem;
  color: #2c3e50;
  text-align: center;
}

.table-wrap {
  width: 100%;
  max-width: min(420px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.felt {
  display: none;
}

.seat {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem 1.25rem;
  border: 2px solid #aac8aa;
  border-radius: 8px;
  background: #fff;
  cursor: text;
  transition: border-color 0.15s;
  text-align: left;
  min-width: 0;
}

.seat:focus-within {
  border-color: #2c3e50;
}

.player-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.6;
}

input {
  padding: 0;
  font-size: 1.15rem;
  font-weight: 600;
  font-family: inherit;
  border: none;
  background: transparent;
  color: #2c3e50;
  outline: none;
  width: 100%;
  text-align: left;
}

input::placeholder {
  color: #bbb;
  font-weight: 400;
}

@media (min-width: 600px) {
  .table-wrap {
    position: relative;
    max-width: clamp(500px, 65vw, 720px);
    display: grid;
    grid-template-columns: minmax(110px, 1fr) minmax(150px, 1.3fr) minmax(110px, 1fr);
    grid-template-rows: auto auto auto;
    grid-template-areas:
      '.    top    .'
      'left center right'
      '.    bottom .';
    gap: 1.25rem;
    align-items: center;
  }

  .felt {
    grid-area: center;
    width: 100%;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    align-content: center;
    gap: 0.35rem;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #3f8a63, #1c5f3d 75%);
    border: 6px solid #17532f;
    box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.15), 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .felt-suit {
    font-size: clamp(1rem, 4vw, 1.5rem);
    line-height: 1;
    color: rgba(255, 255, 255, 0.55);
  }

  .felt-suit.red {
    color: rgba(255, 190, 180, 0.65);
  }

  .seat {
    align-items: center;
    justify-content: center;
    align-self: center;
    padding: 0.65rem 0.5rem;
    text-align: center;
  }

  .seat.top { grid-area: top; }
  .seat.right { grid-area: right; }
  .seat.bottom { grid-area: bottom; }
  .seat.left { grid-area: left; }

  .player-label {
    font-size: clamp(0.75rem, 1.3vw, 0.9rem);
  }

  input {
    text-align: center;
    font-size: clamp(1.1rem, 2vw, 1.4rem);
  }
}

.random-btn-ghost {
  width: 100%;
  max-width: min(600px, 100%);
  padding: 0.75rem;
  font-size: 1rem;
  font-family: inherit;
  background: transparent;
  color: transparent;
  border: 2px solid transparent;
  border-radius: 8px;
  visibility: hidden;
  pointer-events: none;
}

@media (hover: hover) {
  .seat:hover { border-color: #7a9a7a; }
}
</style>
